/**
 * GlobalView — Aircraft Tracker Service
 *
 * Manages camera tracking of a selected aircraft using Cesium's built-in
 * trackedEntity system. This is the most reliable approach because Cesium
 * handles all camera management (orbit lock, following, render timing)
 * internally.
 *
 * How it works:
 * - Creates a hidden Cesium Entity with a CallbackProperty for position
 * - The CallbackProperty reads from AirLayerRenderer's interpolation data
 * - Sets viewer.trackedEntity = entity → Cesium locks camera in orbit mode
 * - User can orbit (drag) and zoom (scroll) but NOT pan
 * - Aircraft always stays centered
 */

import * as Cesium from 'cesium';
import { useGlobeStore } from '../stores/globeStore';
import { useEntityStore } from '../stores/entityStore';
import { getInterpolatedPosition } from './layers/AirLayerRenderer';

// ─── State ───────────────────────────────────────────────────────

let trackerEntity: Cesium.Entity | null = null;
let trackedEntityId: string | null = null;

// ─── Public API ──────────────────────────────────────────────────

/**
 * Start tracking an aircraft.
 * Creates a hidden Cesium Entity and sets it as viewer.trackedEntity.
 * Cesium handles orbit mode, camera following, and render timing.
 */
export function startTracking(entityId: string): void {
  const entity = useEntityStore.getState().entities.get(entityId);
  if (!entity || entity.type !== 'aircraft') return;

  const viewer = useGlobeStore.getState().viewer;
  if (!viewer || viewer.isDestroyed()) return;

  // Compute target position
  const pos = getInterpolatedPosition(entityId) ?? entity.position;
  const targetCartesian = Cesium.Cartesian3.fromDegrees(pos.lon, pos.lat, pos.alt);

  // Compute current camera distance to aircraft
  const camera = viewer.camera;
  const currentRange = Cesium.Cartesian3.distance(camera.positionWC, targetCartesian);

  // Determine a sensible orbit range:
  // - If camera is already close (< 15 km), keep the current distance
  // - Otherwise clamp to a comfortable distance so the aircraft is visible
  const isOnGround = (entity.properties as Record<string, unknown>)['onGround'] === true;
  const MIN_RANGE = isOnGround ? 200 : 500;
  const MAX_RANGE = isOnGround ? 5_000 : 15_000;
  const DEFAULT_RANGE = isOnGround ? 1_000 : 5_000;
  const orbitRange = currentRange <= MAX_RANGE
    ? Math.max(MIN_RANGE, currentRange)
    : DEFAULT_RANGE;

  // Clean up any previous tracking
  stopTracking();

  // Update store state
  useGlobeStore.getState().setTrackedEntity(entityId);
  useGlobeStore.getState().setSelectedEntity(entityId);

  // Save the entity ID for the CallbackProperty
  trackedEntityId = entityId;

  // Create a hidden Cesium Entity with position updated via callback
  trackerEntity = viewer.entities.add({
    position: new Cesium.CallbackProperty(() => {
      if (!trackedEntityId) return targetCartesian;

      const storeEntity = useEntityStore.getState().entities.get(trackedEntityId);
      if (!storeEntity) return targetCartesian;

      const interpPos = getInterpolatedPosition(trackedEntityId) ?? storeEntity.position;
      return Cesium.Cartesian3.fromDegrees(interpPos.lon, interpPos.lat, interpPos.alt);
    }, false) as unknown as Cesium.PositionProperty,
    // No visual — the plane entity from AirLayerRenderer handles rendering
    point: { pixelSize: 1, color: Cesium.Color.TRANSPARENT },
  });

  // Tell Cesium to track this entity — camera locks in orbit mode
  viewer.trackedEntity = trackerEntity;

  // Set a comfortable orbit view angle and distance
  // Use a small delay to let Cesium apply the trackedEntity transform first
  requestAnimationFrame(() => {
    if (!viewer.isDestroyed() && viewer.trackedEntity === trackerEntity) {
      const offset = new Cesium.HeadingPitchRange(
        camera.heading,                           // preserve current heading
        Cesium.Math.toRadians(-30),               // comfortable look-down pitch
        orbitRange,                                // clamped orbit distance
      );
      viewer.scene.camera.lookAt(
        targetCartesian,
        offset,
      );
    }
  });

  viewer.scene.requestRender();
  console.log(`[Tracker] Tracking aircraft: ${entityId} (range=${Math.round(orbitRange)}m)`);
}

/**
 * Stop tracking — release camera back to free movement.
 */
export function stopTracking(): void {
  const viewer = useGlobeStore.getState().viewer;

  useGlobeStore.getState().setTrackedEntity(null);
  trackedEntityId = null;

  if (viewer && !viewer.isDestroyed()) {
    // Unset tracked entity — releases orbit lock
    if (viewer.trackedEntity === trackerEntity) {
      viewer.trackedEntity = undefined;
    }

    // Remove the hidden entity
    if (trackerEntity) {
      viewer.entities.remove(trackerEntity);
    }

    // Reset camera transform to free mode
    viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
  }

  trackerEntity = null;
  console.log('[Tracker] Tracking stopped');
}

/**
 * Check if currently tracking an aircraft.
 */
export function isTracking(): boolean {
  return useGlobeStore.getState().trackedEntityId !== null;
}
